/**
 * Offline-Indicator — tiny glass-floating pill that surfaces network
 * state changes so the user understands why a sync stall happened.
 *
 * Logic intentionally minimal:
 *  - Watches `navigator.onLine` + `online`/`offline` events.
 *  - When offline → shows persistent "Offline" pill (bottom-left).
 *  - When transitioning offline→online → flashes "Wieder online" pill
 *    for 3 seconds then hides.
 *
 * Skipped inside Tauri (always "online" against local DB anyway) and in
 * dev (noisy during HMR reloads).
 */

import { CloudOff, Wifi } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { isTauri } from "../lib/local-media.js";

type State = "online" | "offline" | "recovered";

export function OfflineIndicator() {
  const [state, setState] = useState<State>("online");
  const flashTimer = useRef<number | null>(null);

  useEffect(() => {
    if (isTauri()) return;

    // Initial sync — `navigator.onLine` is a hint, not a guarantee, but
    // good enough to color the first paint correctly.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setState("offline");
    }

    const onOnline = () => {
      setState((prev) => (prev === "offline" ? "recovered" : "online"));
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => {
        setState("online");
      }, 3000);
    };
    const onOffline = () => {
      if (flashTimer.current) {
        window.clearTimeout(flashTimer.current);
        flashTimer.current = null;
      }
      setState("offline");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, []);

  if (state === "online") return null;

  const isOffline = state === "offline";
  return (
    <div
      className="glass-floating fixed bottom-4 left-4 z-255 flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 shadow-2xl"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
        left: "calc(env(safe-area-inset-left, 0px) + 1rem)",
      }}
      role="status"
      aria-live="polite"
    >
      {isOffline ? (
        <>
          <CloudOff className="size-3.5 text-amber-300" />
          <span className="text-[12px] font-medium" style={{ color: "#f4f5f3" }}>
            Offline
          </span>
          <span className="text-[11px]" style={{ color: "var(--color-on-surface-variant)" }}>
            — Änderungen werden später synchronisiert
          </span>
        </>
      ) : (
        <>
          <Wifi className="size-3.5 text-pitch-lime" />
          <span className="text-[12px] font-medium" style={{ color: "#f4f5f3" }}>
            Wieder online
          </span>
        </>
      )}
    </div>
  );
}
